// PublicVoucher.jsx
// This view is accessed via QR code and displays voucher details.
// If the voucher is not yet attached to a customer, the user is prompted to attach it.
import { useState, useCallback } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
    Page,
    Grid,
    Card,
    Text,
    Thumbnail,
    TextField,
    Button,
    Layout,
    Banner,
    BlockStack,
    InlineStack,
    SkeletonPage,
    SkeletonBodyText,
    SkeletonThumbnail,
    IndexTable,
    Filters,
    Box,
} from '@shopify/polaris';
import { FilterIcon, SearchIcon } from '@shopify/polaris-icons';
import Barcode from '#client/components/Barcode/Barcode';
import CustomerAuthButton from '#client/components/common/CustomerAuthButton/CustomerAuthButton';
import wwLogo from '#client/assets/images/ww-logo-allcompany-black.png';
import { describeRequiredChains } from '#shared/ruleGroups';
import './PublicVoucher.css';

function PublicVoucher() {
    const { code: routeCode, uuid } = useParams();
    const [queryValue, setQueryValue] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const baseUrl = import.meta.env.VITE_API_URL;
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');

    const {
        data: voucherData,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['public-voucher', routeCode, uuid],
        enabled: !!routeCode && !!uuid,
        queryFn: async () => {
            const res = await axios.get(`${baseUrl}/api/app/vouchers/${routeCode}/${uuid}`);
            return res.data?.data?.voucher || null;
        },
    });

    // Determine if the voucher is expired
    const isExpired = voucherData?.dateExpires && new Date(voucherData.dateExpires) < new Date();

    // Get the program products from voucher data
    const programProducts = voucherData?.program?.programProducts ?? [];

    // If the program applies to all items, there are no specific eligible products to filter
    const appliesToAllItems = voucherData?.program?.restrictToEligibleProducts === false;

    // A program with a required group splits the list and states the requirement; the rest render as before
    const requirement = voucherData?.requirement;
    const showRequirement = requirement?.applies === true;

    const handleQueryChange = useCallback((value) => setQueryValue(value), []);

    const appliedFilters = [];

    if (minPrice !== '') {
        appliedFilters.push({
            key: 'minPrice',
            label: `Min Price: $${minPrice}`,
            onRemove: () => setMinPrice(''),
        });
    }

    if (maxPrice !== '') {
        appliedFilters.push({
            key: 'maxPrice',
            label: `Max Price: $${maxPrice}`,
            onRemove: () => setMaxPrice(''),
        });
    }
    const filters = [
        {
            key: 'minPrice',
            label: 'Minimum Price',
            filter: <TextField label="Min Price" type="number" value={minPrice} onChange={setMinPrice} autoComplete="off" labelHidden />,
            shortcut: true,
        },
        {
            key: 'maxPrice',
            label: 'Maximum Price',
            filter: <TextField label="Max Price" type="number" value={maxPrice} onChange={setMaxPrice} autoComplete="off" labelHidden />,
            shortcut: true,
        },
    ];

    const filteredProducts = programProducts.filter((programProduct) => {
        const product = programProduct.product || {};
        const matchesSearch = product.title?.toLowerCase().includes(queryValue.toLowerCase());
        // Prices can be numbers or numeric strings; coerce safely
        const ppPrice = programProduct.programPrice; // may be null
        const productMin = Number(product.minVariantPrice);
        const productMax = Number(product.maxVariantPrice);

        // For min filter: use programPrice, else product.minVariantPrice
        const minComparable = ppPrice ?? productMin;

        // For max filter: use programPrice, else product.maxVariantPrice
        const maxComparable = ppPrice ?? productMax;

        // Coerce filter inputs to numbers for comparison
        const minInput = Number(minPrice);
        const maxInput = Number(maxPrice);

        // If no filter is set, or product price meets criteria, it's a match
        const matchesMinPrice = !minPrice || (Number.isFinite(minComparable) && Number.isFinite(minInput) && minComparable >= minInput);
        const matchesMaxPrice = !maxPrice || (Number.isFinite(maxComparable) && Number.isFinite(maxInput) && maxComparable <= maxInput);

        // Set displayPrice for products with a range of prices
        programProduct.displayPrice = productMin.toFixed(2) + ' - ' + productMax.toFixed(2);
        // If product has a single price point, set displayPrice to that value
        if (productMin === productMax) {
            // Product has a single price point
            programProduct.displayPrice = productMin.toFixed(2);
        }

        // Exclude products marked as excluded in the program
        if (programProduct.exclude) {
            return false;
        }

        return matchesSearch && matchesMinPrice && matchesMaxPrice;
    });

    const productTable = (products) => (
        <IndexTable selectable={false} itemCount={products.length} headings={[{ title: 'Product' }, { title: 'Price', alignment: 'end' }]}>
            {products.map((programProduct, index) => (
                <IndexTable.Row id={programProduct.product.id} key={programProduct.product.id} position={index}>
                    <IndexTable.Cell>
                        <InlineStack className="product-card" gap="400" align="start" blockAlign="center">
                            {programProduct.product.image ? <Thumbnail size="small" source={programProduct.product.image} alt={programProduct.product.title} /> : <SkeletonThumbnail size="small" />}
                            <Text variant="bodyMd" fontWeight="bold" as="span" title={programProduct.title}>
                                {programProduct.product.title}
                            </Text>
                        </InlineStack>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                        <Text alignment="end">${programProduct.programPrice ? Number(programProduct.programPrice).toFixed(2) : programProduct.displayPrice || 'N/A'}</Text>
                    </IndexTable.Cell>
                </IndexTable.Row>
            ))}
        </IndexTable>
    );

    if (isError) {
        return (
            <Page title="Error">
                <Text as="p" variant="bodyMd">
                    Failed to load voucher
                </Text>
            </Page>
        );
    }
    //show us a skeleton page while loading
    if (isLoading) {
        return (
            <Page>
                <SkeletonPage primaryAction>
                    <Layout>
                        <Layout.Section>
                            <SkeletonBodyText />
                        </Layout.Section>
                    </Layout>
                </SkeletonPage>
            </Page>
        );
    }
    // If voucher data is not available, show an error message
    if (!voucherData) {
        return (
            <Page title="Error">
                <Text as="p" variant="bodyMd">
                    Failed to load voucher.
                </Text>
            </Page>
        );
    }

    const { voucherCode = '', customerId = null, firstName = '', lastName = '', distEmailAddress = '', dateStart, dateExpires, valueInitial = 0, program = {} } = voucherData;
    const handleCancel = () => {
        window.history.back();
    };

    return (
        <>
            <Box padding="400" style={{ textAlign: 'center', marginTop: 'var(--p-space-200)' }} gap="300">
                <img src={wwLogo} alt="Work World Logo" style={{ width: 'var(--p-size-500)' }} />
            </Box>
            <Page title="Voucher Details" fullWidth>
                {isExpired && (
                    <div className="banner-wrapper">
                        <Banner title="This voucher has expired" tone="critical">
                            <p>This voucher has expired and can no longer be redeemed. Please contact your employer to get a new voucher issued.</p>
                        </Banner>
                    </div>
                )}
                <Grid columns={12} gap="400">
                    <Grid.Cell columnSpan={{ xs: 6, lg: 8 }}>
                        <div className="voucher-card">
                            <Card sectioned>
                                <BlockStack gap="200">
                                    <Text variant="headingMd">Voucher Code: {voucherCode}</Text>

                                    <Text>Description: {program?.programDescription}</Text>

                                    {/* Product Discount */}
                                    {program?.discountType && program?.discountAmount != null && Number(program.discountAmount) > 0 && (
                                        <Text>
                                            Product Discount: {program.discountType === 'percentage' ? `${Number(program.discountAmount)}% off` : `$${Number(program.discountAmount).toFixed(2)} off`}
                                        </Text>
                                    )}
                                    {/* Program Credit */}
                                    {program?.subsidyType &&
                                        ['percentage', 'percentage-subsidy', 'total-value', 'flat-subsidy'].includes(program.subsidyType) &&
                                        valueInitial != null &&
                                        Number(valueInitial) > 0 && (
                                            <Text>
                                                Program Credit:{' '}
                                                {['percentage', 'percentage-subsidy'].includes(program.subsidyType) ? `${Number(valueInitial)}%` : `$${Number(valueInitial).toFixed(2)}`}
                                            </Text>
                                        )}
                                    {/* Free shipping */}
                                    {program?.freeShipping && <Text>Free Shipping: Included</Text>}

                                    {/* Tax Exempt */}
                                    {program?.taxExempt && <Text>Tax Exempt: Included</Text>}

                                    <Text>
                                        Recipient: {firstName} {lastName}
                                    </Text>

                                    <Text>Email: {distEmailAddress}</Text>

                                    <Text>Start Date: {dateStart ? new Date(dateStart).toLocaleDateString() : 'N/A'}</Text>

                                    <Text>Expiration Date: {dateExpires ? new Date(dateExpires).toLocaleDateString() : 'N/A'}</Text>

                                    <InlineStack gap="200" align="end">
                                        <div className="custom-button-wrapper">
                                            {customerId === null && (
                                                <CustomerAuthButton voucherCode={voucherCode} uuid={uuid} disabled={isExpired}>
                                                    Attach to Account
                                                </CustomerAuthButton>
                                            )}
                                        </div>
                                        <Button onClick={handleCancel}>Cancel</Button>
                                    </InlineStack>
                                </BlockStack>
                            </Card>
                        </div>
                    </Grid.Cell>

                    <Grid.Cell columnSpan={{ xs: 6, lg: 4 }}>
                        <div className="barcode-card">
                            <Card sectioned>
                                <Box paddingBlockStart="600" paddingBlockEnd="500">
                                    {isExpired ? (
                                        <Banner tone="critical" title="Barcode Unavailable">
                                            <p>This voucher has expired.</p>
                                        </Banner>
                                    ) : (
                                        <Box paddingBlockStart="600" paddingBlockEnd="800">
                                            <Barcode code={voucherCode} />
                                        </Box>
                                    )}
                                </Box>
                            </Card>
                        </div>
                    </Grid.Cell>
                </Grid>

                <Card title="Eligible Products" style={{ marginTop: '2rem' }}>
                    <Text variant="headingMd" as="h2" style={{ padding: '1rem 1rem 0 1rem' }}>
                        Eligible Products
                    </Text>
                    {appliesToAllItems ? (
                        <Box padding="400">
                            <Text as="p" variant="bodyMd">
                                Eligible for all products, excluding Gift Card purchases.
                            </Text>
                        </Box>
                    ) : (
                        <>
                            {showRequirement && (
                                <Box padding="400">
                                    <Banner tone={requirement.met ? 'success' : 'info'}>
                                        <p>
                                            {requirement.met
                                                ? // Earlier purchases that were never captured count as met without saying what was bought
                                                  requirement.historyUnknown
                                                    ? 'This voucher can be used on any eligible item.'
                                                    : 'A required item has been bought with this voucher, so the remaining balance can be used on any eligible item.'
                                                : `Buy ${describeRequiredChains(requirement.requiredChains)} with this voucher. After that, the remaining balance can be used on any eligible item.`}
                                        </p>
                                    </Banner>
                                </Box>
                            )}
                            <InlineStack align="end" gap="200" style={{ padding: '1rem' }}>
                                <Button icon={SearchIcon} onClick={() => setShowSearch(!showSearch)}></Button>
                                <Button icon={FilterIcon} onClick={() => setShowFilters(!showFilters)}></Button>
                            </InlineStack>
                            <div className="product-filters">
                                {(showSearch || showFilters) && (
                                    <Filters
                                        queryValue={queryValue}
                                        onQueryChange={handleQueryChange}
                                        onClearAll={() => {
                                            setQueryValue('');
                                            setMinPrice('');
                                            setMaxPrice('');
                                        }}
                                        filters={showFilters ? filters : []}
                                        appliedFilters={appliedFilters}
                                    />
                                )}
                            </div>

                            {showRequirement ? (
                                <BlockStack gap="400">
                                    <Box paddingInline="400">
                                        <Text variant="headingSm" as="h3">
                                            Required items
                                        </Text>
                                    </Box>
                                    {productTable(filteredProducts.filter((programProduct) => programProduct.required))}
                                    <Box paddingInline="400">
                                        <Text variant="headingSm" as="h3">
                                            Optional items
                                        </Text>
                                    </Box>
                                    {productTable(filteredProducts.filter((programProduct) => !programProduct.required))}
                                </BlockStack>
                            ) : (
                                productTable(filteredProducts)
                            )}
                        </>
                    )}
                </Card>
            </Page>
        </>
    );
}

export default PublicVoucher;
